import { UserRound } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useSession } from "../../features/auth/useSession";
import { cn } from "../../lib/cn";
import { StorePicker } from "../../features/staff/StorePicker";
import { NAV_GROUPS } from "./navigation";

const UserFooter = () => {
	const { data } = useSession();
	if (!data) return null;

	const fullName = [data.user.firstName, data.user.lastName].filter(Boolean).join(" ");

	return (
		<div className="flex items-center gap-2.5 border-t border-line px-3 py-3">
			<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-raised">
				<UserRound className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
			</div>
			<div className="min-w-0">
				<p className="truncate text-xs font-medium text-ink">{fullName || data.user.email}</p>
				<p className="truncate text-xs text-ink-faint">
					{fullName ? data.user.email : data.user.accountOwner ? "Account owner" : "Staff"}
				</p>
			</div>
		</div>
	);
};

export const Sidebar = ({ onNavigate }: { onNavigate?: () => void }) => (
	<aside className="flex h-full w-64 shrink-0 flex-col bg-canvas/60 backdrop-blur-xl">
		<div className="flex h-14 items-center gap-2.5 px-5">
			<img
				src="/logo.svg"
				alt=""
				width={26}
				height={26}
				className="shrink-0 rounded-[7px] shadow-[0_2px_6px_rgba(15,32,64,0.28)]"
			/>
			<span className="text-sm font-semibold tracking-tight text-ink">AOVtools</span>
		</div>

		<nav className="flex flex-1 flex-col gap-6 overflow-y-auto px-3 pt-2 pb-4">
			{NAV_GROUPS.map((group, index) => (
				<div key={group.label ?? index} className={cn("space-y-1", group.bottom && "mt-auto")}>
					{group.label ? (
						<p className="px-2 pb-1 text-xs font-medium text-ink-faint">{group.label}</p>
					) : null}
					{group.items.map((item) => (
						<div key={item.to} className="space-y-0.5">
							<NavLink
								to={item.to}
								end={item.end}
								onClick={onNavigate}
								className={({ isActive }) =>
									cn(
										"flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
										isActive
											? "bg-accent-soft font-medium text-accent"
											: "text-ink-muted hover:bg-raised hover:text-ink",
									)
								}
							>
								<item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
								{item.label}
							</NavLink>

							{item.children?.map((child) => (
								<NavLink
									key={child.to}
									to={child.to}
									end={child.end}
									onClick={onNavigate}
									className={({ isActive }) =>
										cn(
											"block rounded-md py-1 pr-2 pl-9 text-xs transition-colors",
											isActive
												? "bg-accent-soft font-medium text-accent"
												: "text-ink-faint hover:bg-raised hover:text-ink-muted",
										)
									}
								>
									{child.label}
								</NavLink>
							))}
						</div>
					))}
				</div>
			))}
		</nav>

		<StorePicker />
		<UserFooter />
	</aside>
);
